import { useState } from "react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useSEO } from "@/hooks/useCanonical";
import { getStaticRouteSeo } from "@/lib/seo";
import { buildBookingJsonLd } from "@/lib/structuredData";

const Booking = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const seo = getStaticRouteSeo("booking");

  useSEO({
    ...seo,
    jsonLd: buildBookingJsonLd(),
  });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    jobType: "",
    message: "",
    preferredDates: [] as Date[],
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const preferredDateStr =
      formData.preferredDates.length > 0
        ? formData.preferredDates.map((d) => format(d, "d MMMM yyyy", { locale: sv })).join(", ")
        : undefined;

    try {
      const { data, error } = await supabase.functions.invoke("send-booking-email", {
        body: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || undefined,
          jobType: formData.jobType || undefined,
          message: formData.message,
          preferredDates: preferredDateStr,
        },
      });

      if (error) throw error;

      // Track conversion in GA4
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', 'generate_lead', {
          event_category: 'booking',
          event_label: formData.jobType || 'unspecified',
          value: 1
        });
      }

      toast.success("Förfrågan skickad!", {
        description: `Tack för din förfrågan! En bekräftelse har skickats till ${formData.email}. Jag återkommer så snart som möjligt.`,
      });
      
      setFormData({
        name: "",
        email: "",
        phone: "",
        jobType: "",
        message: "",
        preferredDates: [],
      });
    } catch (error) {
      console.error("Error sending booking email:", error);
      toast.error("Något gick fel", {
        description: "Kunde inte skicka förfrågan. Försök igen senare.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      <main className="flex-1 pt-32 pb-16">
        <div className="container mx-auto px-6 max-w-4xl">
          <h1 className="text-3xl md:text-4xl font-small-heading font-extrabold italic text-foreground mb-4">
            Skicka boknings/kontakt-förfrågan
          </h1>
          <p className="text-muted-foreground mb-8">
            Fyll i formuläret nedan så kontaktar jag dig så snart som möjligt. Det är inte bindande, vi bestämmer allt
            tillsammans. Ifall jag inte svarat inom två vardagar, kolla om mitt mail hamnat i skräpposten.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Namn *</Label>
                <Input
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      name: e.target.value,
                    })
                  }
                  placeholder="Ditt namn"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      email: e.target.value,
                    })
                  }
                  placeholder="din@email.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Telefon</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      phone: e.target.value,
                    })
                  }
                  placeholder="070-123 45 67"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="preferredDate">Önskedatum (du kan välja flera)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      id="preferredDate"
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal h-auto min-h-10",
                        formData.preferredDates.length === 0 && "text-muted-foreground",
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      {formData.preferredDates.length > 0 ? (
                        <span className="truncate">
                          {formData.preferredDates.map((d) => format(d, "d MMM", { locale: sv })).join(", ")}
                        </span>
                      ) : (
                        <span>Välj datum</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="multiple"
                      selected={formData.preferredDates}
                      onSelect={(dates) => setFormData({ ...formData, preferredDates: dates || [] })}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                      locale={sv}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="jobType">Typ av fotografering</Label>
              <Input
                id="jobType"
                value={formData.jobType}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    jobType: e.target.value,
                  })
                }
                placeholder="T.ex. porträtt-, produkt-, mamma-fotografering..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Meddelande *</Label>
              <Textarea
                id="message"
                required
                value={formData.message}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    message: e.target.value,
                  })
                }
                placeholder="Berätta mer om vad du önskar..."
                rows={6}
              />
            </div>

            <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Skickar..." : "Skicka förfrågan"}
            </Button>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};
export default Booking;
